import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import type { KipuSyncStatus } from "@athena/common/graphql/generated/schema";
import { Button, Text } from "@athena/ui";
import { ClientsDocument } from "@/graphql/clients.generated";
import { LatestKipuSyncRunDocument, SyncKipuDocument } from "@/graphql/kipu.generated";
import { formatContactTime } from "../helpers/client";

/** How often the run is re-read while one is in progress. Every run reads every chart, which takes minutes. */
export const KIPU_SYNC_POLL_MS = 5_000;

/** How long a pressed sync has to appear before the button says it did not start. */
export const KIPU_SYNC_START_TIMEOUT_MS = 60_000;

/** PARTIAL is in progress too: a long run saves its place and carries on in a fresh invocation. */
const inProgress = (status: KipuSyncStatus | undefined): boolean => status === "RUNNING" || status === "PARTIAL";

/**
 * "Sync from KIPU" on the alumni directory, for a coordinator who does not want to wait for the
 * scheduled run (ADR 0012).
 *
 * Starts the same job the schedule does, then follows it: the latest run is polled while one is in
 * progress, and when it succeeds the alumni list is refetched, so new and changed alumni appear
 * without a reload. A run already going — the schedule's, or another coordinator's — is followed the
 * same way. The sync's lock means pressing twice starts one run; the server answers false to the
 * second.
 */
export function KipuSyncControl() {
    const { t } = useTranslation();
    const apollo = useApolloClient();
    /**
     * Set by a press that started a run: the id of the newest run before it (null for none). The run
     * starts asynchronously, so until a newer one appears there is nothing to follow but the wait.
     */
    const [
        startedAfter,
        setStartedAfter,
    ] = useState<string | null | undefined>();
    /** Whether a run has been seen in progress (or started) and its outcome not yet reported. */
    const following = useRef<boolean>(false);
    const { data, refetch, startPolling, stopPolling } = useQuery(LatestKipuSyncRunDocument);
    const [
        syncKipu,
        { loading: starting },
    ] = useMutation(SyncKipuDocument);

    const latest = data?.kipuSyncRuns[0];
    // Explicitly false only. While the query loads, the button stays as it was rather than flashing off.
    const off = data?.kipuSyncEnabled === false;
    const waiting = startedAfter !== undefined && (latest?.id ?? null) === startedAfter;
    const busy = waiting || inProgress(latest?.status);

    useEffect(() => {
        if (busy) startPolling(KIPU_SYNC_POLL_MS);
        else stopPolling();
    }, [
        busy,
        startPolling,
        stopPolling,
    ]);

    // Report a followed run once it ends, and on success refresh the list it changed.
    useEffect(() => {
        if (busy) {
            following.current = true;
            return;
        }
        if (!following.current || !latest) return;
        following.current = false;
        if (latest.status === "SUCCEEDED") {
            toast.success(t("admin.alumni.directory.sync.done"));
            void apollo.refetchQueries({ include: [ClientsDocument] });
        } else {
            toast.error(t("admin.alumni.directory.sync.failed"));
        }
    }, [
        busy,
        latest,
        apollo,
        t,
    ]);

    // A started run that never appears would leave the button spinning forever.
    useEffect(() => {
        if (!waiting) return;
        const timer = setTimeout(() => {
            following.current = false;
            setStartedAfter(undefined);
            toast.error(t("admin.alumni.directory.sync.notStarted"));
        }, KIPU_SYNC_START_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [
        waiting,
        t,
    ]);

    const start = async () => {
        try {
            const result = await syncKipu();
            if (result.data?.syncKipu) {
                setStartedAfter(latest?.id ?? null);
            } else {
                toast.info(t("admin.alumni.directory.sync.alreadyRunning"));
                // Read the run that holds the lock, so it is followed like one started here.
                await refetch();
            }
        } catch {
            toast.error(t("admin.alumni.directory.sync.startFailed"));
        }
    };

    const status = off
        ? t("admin.alumni.directory.sync.off")
        : busy
          ? t("admin.alumni.directory.sync.syncing")
          : latest?.status === "SUCCEEDED"
            ? t("admin.alumni.directory.sync.lastSynced", {
                  time: formatContactTime(latest.finishedAt ?? latest.startedAt),
              })
            : latest?.status === "FAILED"
              ? t("admin.alumni.directory.sync.lastFailed", {
                    time: formatContactTime(latest.finishedAt ?? latest.startedAt),
                })
              : t("admin.alumni.directory.sync.never");

    return (
        <div className="flex flex-col items-start gap-1 sm:items-end">
            <Button
                variant="outline"
                loading={starting || busy}
                disabled={off}
                leftIcon={<RefreshCw className="h-4 w-4" aria-hidden />}
                onClick={start}
            >
                {t("admin.alumni.directory.sync.button")}
            </Button>
            <Text size="xs" muted aria-live="polite">
                {status}
            </Text>
        </div>
    );
}
